/**
 * Filter fields for the /feedback queue — the same chip-and-query surface the
 * deals list uses, pointed at feedback tickets.
 *
 * WHY HAND-WRITTEN RATHER THAN METADATA-DRIVEN
 * `buildDealFilterFields` reads the deal metadata endpoint because the deal
 * schema is 56 columns of lookups that change. Feedback is one small table
 * with three closed vocabularies that already live in this app as
 * STATUS_META / KIND_META / SEVERITY_META. Fetching a metadata document to
 * rediscover six enum values we render tags from anyway would be a round trip
 * to learn what the page already knows.
 *
 * The output is the shape `WigFilterPanel` wants, and the queries it emits go
 * through `filterRows` (client-side) rather than to the server: the feedback
 * list arrives whole and small, and there is no POST /feedback_requests/search
 * to send them to.
 */

/**
 * @param {object}  opts
 * @param {object}  opts.statusMeta    { value: { label } } — status vocabulary
 * @param {object}  opts.kindMeta      { value: { label } } — kind vocabulary
 * @param {object}  opts.severityMeta  { value: { label } } — severity vocabulary
 * @param {boolean} opts.isAdmin       reporter columns are admin-only
 * @returns {Array} WigFilterField[]
 */
export const buildFeedbackFilterFields = ({
  statusMeta = {},
  kindMeta = {},
  severityMeta = {},
  isAdmin = false,
} = {}) => {
  const optionsOf = (meta) =>
    Object.entries(meta).map(([value, m]) => ({
      label: m?.label ?? value,
      value,
    }));

  return [
    // "select" over "singleSelect" deliberately: it offers is / is not / is
    // any of / is none of, and "show me New and Triaged" is the common triage
    // question. See filterFields.js for the same reasoning on deals.
    { name: "status", label: "Status", inputType: "select", values: optionsOf(statusMeta) },
    { name: "kind", label: "Type", inputType: "select", values: optionsOf(kindMeta) },
    {
      name: "severity",
      label: "Severity",
      inputType: "select",
      values: optionsOf(severityMeta),
    },
    { name: "title", label: "Summary", inputType: "text" },
    // The point of putting the description in the grid is that it becomes
    // searchable and exportable; without this field it is only exportable.
    { name: "description", label: "Details", inputType: "text" },
    { name: "status_note", label: "Status note", inputType: "text" },
    // Who filed it is only offered to someone who can see other people's.
    ...(isAdmin
      ? [
          { name: "reported_by_name", label: "Reported by (name)", inputType: "text" },
          { name: "reported_by", label: "Reported by (email)", inputType: "text" },
        ]
      : []),
    { name: "app_route", label: "Route", inputType: "text" },
    { name: "app_version", label: "App version", inputType: "text" },
    { name: "comment_count", label: "Replies", inputType: "number" },
    // The browser-side stamp, matching the grid's "Reported" column. Compared
    // by calendar day — see filterQuery.js.
    { name: "client_reported_at", label: "Reported", inputType: "date" },
  ];
};

export default buildFeedbackFilterFields;
